import React from 'react';
import { Button, IconButton, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledButton = styled(Button)(({ theme, variant = 'contained', size = 'medium', color = 'primary' }) => {
  const gradients = {
    primary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      hover: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    },
    secondary: {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      hover: 'linear-gradient(135deg, #e085e8 0%, #e54d62 100%)',
    },
    success: {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      hover: 'linear-gradient(135deg, #3e9bed 0%, #00e1ed 100%)',
    },
    warning: {
      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      hover: 'linear-gradient(135deg, #e95f89 0%, #edcf30 100%)',
    },
    error: {
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      hover: 'linear-gradient(135deg, #ed898d 0%, #edbfdf 100%)',
    },
    info: {
      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      hover: 'linear-gradient(135deg, #97dcd9 0%, #edc5d2 100%)',
    },
  };

  const sizes = {
    small: {
      padding: '6px 16px',
      fontSize: '0.75rem',
      minHeight: '32px',
    },
    medium: {
      padding: '8px 24px',
      fontSize: '0.875rem',
      minHeight: '40px',
    },
    large: {
      padding: '12px 32px',
      fontSize: '1rem',
      minHeight: '48px',
    },
  };

  const variants = {
    contained: {
      background: gradients[color].background,
      color: '#ffffff',
      border: 'none',
      boxShadow: theme.shadows[2],
      '&:hover': {
        background: gradients[color].hover,
        boxShadow: theme.shadows[4],
        transform: 'translateY(-2px)',
      },
      '&:active': {
        transform: 'translateY(0px)',
      },
    },
    outlined: {
      background: 'transparent',
      color: theme.palette[color].main,
      border: `2px solid ${theme.palette[color].main}`,
      '&:hover': {
        background: theme.palette[color].main,
        color: '#ffffff',
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      },
    },
    text: {
      background: 'transparent',
      color: theme.palette[color].main,
      border: 'none',
      '&:hover': {
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'rgba(0, 0, 0, 0.04)',
        transform: 'translateY(-1px)',
      },
    },
    glass: {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(255, 255, 255, 0.2)',
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.2)' 
        : 'rgba(255, 255, 255, 0.3)'}`,
      backdropFilter: 'blur(10px)',
      '&:hover': {
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.2)' 
          : 'rgba(255, 255, 255, 0.3)',
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      },
    },
  };

  return {
    borderRadius: theme.shape.borderRadius * 2,
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    ...sizes[size],
    ...variants[variant],
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s',
    },
    
    '&:hover::before': {
      left: '100%',
    },
    
    '&:disabled': {
      opacity: 0.6,
      transform: 'none !important',
    },
  };
});

const StyledIconButton = styled(IconButton)(({ theme, color = 'primary', size = 'medium' }) => {
  const gradients = {
    primary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      hover: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    },
    secondary: {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      hover: 'linear-gradient(135deg, #e085e8 0%, #e54d62 100%)',
    },
    success: {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      hover: 'linear-gradient(135deg, #3e9bed 0%, #00e1ed 100%)',
    },
    warning: {
      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      hover: 'linear-gradient(135deg, #e95f89 0%, #edcf30 100%)',
    },
    error: {
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      hover: 'linear-gradient(135deg, #ed898d 0%, #edbfdf 100%)',
    },
    info: {
      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      hover: 'linear-gradient(135deg, #97dcd9 0%, #edc5d2 100%)',
    },
  };

  const sizes = {
    small: { width: 32, height: 32 },
    medium: { width: 40, height: 40 },
    large: { width: 48, height: 48 },
  };

  return {
    borderRadius: '50%',
    background: gradients[color].background,
    color: '#ffffff',
    boxShadow: theme.shadows[2],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...sizes[size],
    
    '&:hover': {
      background: gradients[color].hover,
      transform: 'scale(1.1)',
      boxShadow: theme.shadows[4],
    },
    
    '&:active': {
      transform: 'scale(0.95)',
    },
  };
});

const ModernButton = ({ 
  children, 
  variant = 'contained', 
  color = 'primary', 
  size = 'medium',
  startIcon,
  endIcon,
  loading = false,
  sx = {},
  ...props 
}) => {
  const theme = useTheme();

  return (
    <StyledButton
      variant={variant}
      color={color}
      size={size}
      startIcon={startIcon}
      endIcon={endIcon}
      disabled={loading}
      sx={sx}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

const ModernIconButton = ({ 
  children, 
  color = 'primary', 
  size = 'medium',
  sx = {},
  ...props 
}) => {
  return (
    <StyledIconButton
      color={color}
      size={size}
      sx={sx}
      {...props}
    >
      {children}
    </StyledIconButton>
  );
};

// Specialized button variants
export const GradientButton = ({ children, color = 'primary', ...props }) => (
  <ModernButton variant="contained" color={color} {...props}>
    {children}
  </ModernButton>
);

export const GlassButton = ({ children, ...props }) => (
  <ModernButton variant="glass" {...props}>
    {children}
  </ModernButton>
);

export const ActionButton = ({ 
  icon, 
  label, 
  color = 'primary', 
  variant = 'contained',
  ...props 
}) => (
  <ModernButton
    variant={variant}
    color={color}
    startIcon={icon}
    {...props}
  >
    {label}
  </ModernButton>
);

export const FloatingActionButton = ({ 
  icon, 
  color = 'primary', 
  size = 'large',
  sx = {},
  ...props 
}) => {
  const theme = useTheme();
  
  return (
    <ModernIconButton
      color={color}
      size={size}
      sx={{
        position: 'fixed',
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        zIndex: theme.zIndex.fab,
        boxShadow: theme.shadows[8],
        '&:hover': {
          boxShadow: theme.shadows[12],
        },
        ...sx,
      }}
      {...props}
    >
      {icon}
    </ModernIconButton>
  );
};

export default ModernButton; 